import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { ConfirmationContainer } from "../../../../../Modal/Confirmation/Confirmation";
import { InvestigationButton } from "../../components";
import { EGSwitch } from "../../../../../MainComponents/Switch/Switch";
import { updateStatusCloseDetails } from "../../../../../../apis";
import dayjs from "dayjs";

export const Conclude = ({
  show,
  onHide,
  buildConclusion,
  handleSaveConclusionDetails,
  conclusion,
  assignsDetails = [],
  onReloadReport,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const hasMultipleReports = assignsDetails.length > 1;

  const [conclusion_at, setConclusion_at] = useState(
    dayjs(conclusion?.conclusion_at).isValid()
      ? dayjs(conclusion?.conclusion_at)
      : dayjs()
  );

  // Estado para cerrar denuncias vinculadas
  const [closeLinkedReports, setCloseLinkedReports] = useState(false);
  const [closeAllReports, setCloseAllReports] = useState(true);
  const [selectedReports, setSelectedReports] = useState([]);
  const [closeType, setCloseType] = useState("founded");
  const [isLoading, setIsLoading] = useState(false);

  // Opciones de tipo de cierre
  const closeTypeOptions = [
    { label: t("Conclusion.closed_founded"), value: "founded" },
    { label: t("Conclusion.closed_unfounded"), value: "unfounded" },
    { label: t("Conclusion.closed_undetermined"), value: "undefined" },
  ];

  useEffect(() => {
    if (show) {
      setConclusion_at(
        dayjs(conclusion?.conclusion_at).isValid()
          ? dayjs(conclusion?.conclusion_at)
          : dayjs()
      );
      // Reset estados al abrir el modal
      setCloseLinkedReports(false);
      setCloseAllReports(true);
      setSelectedReports([]);
      setCloseType("founded");
      setIsLoading(false);
    }
  }, [show]);

  // Manejar selección individual de reportes
  const handleReportSelection = (reportId) => {
    setSelectedReports((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId]
    );
  };

  // Obtener los reportes a cerrar
  const getReportsToClose = () => {
    if (!closeLinkedReports) return [];
    if (closeAllReports) return assignsDetails.map((r) => r.id);
    return selectedReports;
  };

  const handleSave = async () => {
    setIsLoading(true);

    const objConclusion = {
      conclusion: conclusion.conclusion ?? "",
      comment: conclusion.comment ?? "",
      conclusion_at: conclusion_at.toDate() ?? null,
    };

    try {
      // Guardar la conclusión de la investigación
      await handleSaveConclusionDetails(objConclusion);

      // Cerrar las denuncias seleccionadas si aplica
      const reportsToClose = getReportsToClose();
      if (reportsToClose.length > 0) {
        const closeDetails = {
          closed: closeType,
          close_date: conclusion_at.toDate(),
          observations: "",
          files: [],
          followup: false,
          causes: [],
          another_causes: [],
        };

        // Cerrar cada reporte seleccionado
        const closePromises = reportsToClose.map((reportId) =>
          updateStatusCloseDetails(reportId, closeDetails)
        );

        await Promise.all(closePromises);

        enqueueSnackbar(
          t("Conclusion.reports_closed_successfully", {
            count: reportsToClose.length,
          }),
          { variant: "success" }
        );

        // Recargar el reporte para actualizar la UI
        if (onReloadReport) {
          onReloadReport();
        }
      }

      onHide();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("Conclusion.error_closing_reports"), {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationContainer
      show={show}
      onHide={onHide}
      variant="error"
      ethiquin="investigation"
      sx={{ p: 2 }}
    >
      <Typography
        fontSize={28}
        fontWeight={700}
        color="pink.main"
        textAlign="start"
      >
        {t("Conclusion.conclude_modal_title")}
      </Typography>
      <Typography
        component="p"
        color="oxfordBlue.main"
        mt={2}
        textAlign="start"
      >
        {t(
          "Conclusion.conclude_modal_description"
        )}
      </Typography>
      <Typography
        component="p"
        color="oxfordBlue2.main"
        mt={1}
        mb={2}
        textAlign="start"
      >
        {t(
          "Conclusion.conclude_modal_description2"
        )}
      </Typography>
      <EGDatePicker
        label={t("Conclusion.conclude_modal_date")}
        value={conclusion_at}
        onChange={setConclusion_at}
      />

      {/* Sección para cerrar denuncias vinculadas - solo si hay más de 1 */}
      {hasMultipleReports && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.blueGray200.main}`,
            backgroundColor: theme.palette.blueGray50.main,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              fontSize={14}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("Conclusion.close_linked_reports")}
            </Typography>
            <EGSwitch
              size="small"
              checked={closeLinkedReports}
              onChange={(e) => setCloseLinkedReports(e.target.checked)}
            />
          </Stack>

          <Collapse in={closeLinkedReports}>
            <Box sx={{ mt: 2 }}>
              {/* Selector de tipo de cierre */}
              <Box sx={{ mb: 2 }}>
                <EGBasicSelect
                  label={t("Conclusion.select_close_type")}
                  value={closeType}
                  onChange={(e) => setCloseType(e.target.value)}
                  options={closeTypeOptions}
                  showHelpText={false}
                  size="small"
                  sx={{ backgroundColor: theme.palette.white.main }}
                />
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  color={theme.palette.oxfordBlue.main}
                >
                  {t("Conclusion.close_all_reports")}
                </Typography>
                <EGSwitch
                  size="small"
                  checked={closeAllReports}
                  onChange={(e) => setCloseAllReports(e.target.checked)}
                />
              </Stack>

              <Collapse in={!closeAllReports}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.white.main,
                    border: `1px solid ${theme.palette.blueGray200.main}`,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    color={theme.palette.blueGray600.main}
                    mb={1}
                  >
                    {t("Conclusion.select_reports_to_close")}
                  </Typography>
                  <Stack spacing={0.5}>
                    {assignsDetails.map((report) => (
                      <FormControlLabel
                        key={report.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedReports.includes(report.id)}
                            onChange={() =>
                              handleReportSelection(report.id)
                            }
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontSize={13} fontWeight={600} color="primary">
                              {report.folio}
                            </Typography>
                            <Typography fontSize={12} color="secondary">
                              | {report.classification_label}
                            </Typography>
                          </Stack>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Collapse>
        </Box>
      )}

      <Typography
        color="primary.main"
        mt={1.5}
        mb={3.5}
        textAlign="start"
        fontSize={16}
        fontWeight={600}
      >
        {t("Conclusion.conclude_modal_confirm")}
      </Typography>
      <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
        <InvestigationButton
          label={t("Conclusion.cancel")}
          onClick={onHide}
          icon={false}
          variant="main"
          size="medium"
          outlined
          disabled={isLoading}
        />
        <InvestigationButton
          label={t("Conclusion.confirm")}
          onClick={handleSave}
          icon={false}
          variant="main"
          size="medium"
          loading={isLoading}
        />
      </Stack>
    </ConfirmationContainer>
  );
};
